import React, { useState, useEffect } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { Card, CardBody, CardHeader } from "../../components/card/card";
import { meter, meterReading } from "../../api";
import { toast } from "react-toastify";
import { useAuth } from "../../contexts/AuthContext";
import LoadingIndicator from "../../components/LoadingIndicator";
import ImageViewer from "../../components/ImageViewer";
import ConfirmationDlg from "../../components/ConfirmationDlg";
import { format, subDays, subWeeks, subMonths, subYears, startOfDay, endOfDay, subHours } from "date-fns";
import { Badge } from "react-bootstrap";  
import { formatDateOnly, renderImage } from "../../utils/helper";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import { Line } from "react-chartjs-2";
import { getStatusColor } from "../../utils/helper";
import NormalInput from "../../components/form/HorizontalNormalInput";
import NormalTextArea from "../../components/form/HorizontalNormalTextarea";
import { PermissionChecker } from "../../components/PermissionChecker";

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
);

function MeterDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [meterData, setMeterData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [readings, setReadings] = useState([]);
  const [chartLoading, setChartLoading] = useState(false);
  const [timeRange, setTimeRange] = useState("1W");
  const [showCustomDateModal, setShowCustomDateModal] = useState(false);
  const [customDateRange, setCustomDateRange] = useState({
    start_date: format(subWeeks(new Date(), 1), 'yyyy-MM-dd'),
    end_date: format(new Date(), 'yyyy-MM-dd')
  });
  const { config } = useAuth();
  const [attachments, setAttachments] = useState([]);
  const [openModal, setOpenModal] = useState(false);

  const [showAddReading, setShowAddReading] = useState(false);

  const [newReading, setNewReading] = useState({
    reading_value: "",
    reading_date: new Date().toISOString().split('T')[0],
    notes: "",
  });

  const timeRangeOptions = [
    { value: "1H", label: "1H" },
    { value: "1D", label: "1D" },
    { value: "1W", label: "1W" },
    { value: "1M", label: "1M" },
    { value: "3M", label: "3M" },
    { value: "6M", label: "6M" },
    { value: "1Y", label: "1Y" },
    { value: "Custom", label: "Custom" },
  ];

  const getDateRange = (range) => {
    const now = new Date();
    switch (range) {
      case "1H":
        return { start: subHours(now, 1), end: now };
      case "1D":
        return { start: subDays(now, 1), end: now };
      case "1W":
        return { start: subWeeks(now, 1), end: now };
      case "1M":
        return { start: subMonths(now, 1), end: now };
      case "3M":
        return { start: subMonths(now, 3), end: now };
      case "6M":
        return { start: subMonths(now, 6), end: now };
      case "1Y":
        return { start: subYears(now, 1), end: now };
      case "Custom":
        return { 
          start: new Date(customDateRange.start_date), 
          end: new Date(customDateRange.end_date) 
        };
      default:
        return { start: subWeeks(now, 1), end: now };
    }
  };

  // Function to determine reading status based on thresholds
  const getReadingStatus = (readingValue) => {
    if (!readingValue || !meterData) return { status: 'Unknown', color: 'secondary', icon: '❓' };
    
    const value = parseFloat(readingValue);
    const warningThreshold = meterData.warning_threshold ? parseFloat(meterData.warning_threshold) : null;
    const alarmThreshold = meterData.alarm_threshold ? parseFloat(meterData.alarm_threshold) : null;
    
    // Check alarm threshold first (highest priority)
    if (alarmThreshold && value >= alarmThreshold) {
      return { status: 'ALARM', color: 'danger', icon: '🚨' };
    }
    
    // Check warning threshold
    if (warningThreshold && value >= warningThreshold) {
      return { status: 'WARNING', color: 'warning', icon: '⚠️' };
    }
    
    // If no thresholds set or value is below thresholds
    if (!warningThreshold && !alarmThreshold) {
      return { status: 'No Thresholds Set', color: 'info', icon: 'ℹ️' };
    }
    
    // Value is normal (below all thresholds)
    return { status: 'NORMAL', color: 'success', icon: '✅' };
  };

  const handleCustomDateSubmit = () => {
    setTimeRange("Custom");
    setShowCustomDateModal(false);
  };

  const handleTimeRangeClick = (range) => {
    if (range === "Custom") {
      setShowCustomDateModal(true);
    } else {
      setTimeRange(range);
    }
  };

  async function getMeterData() {
    try {
      setLoading(true);
      const res = await meter.getOne(id);
      if (res.ok) {
        const body = await res.json();
        setMeterData(body.data);
        const data = body.data;

        if (data.attachments) {
          // check if data.attachments is an object
          if (typeof data.attachments !== "object") {
            console.error("attachments is not an object");
            return;
          }
          const att = [];
          const attReq = [];
          if (data.attachments)
            Object.keys(data.attachments).forEach((key) => {
              if (data.attachments[key]) {
                const name = key.split("_")[3];
                const type = key.split("_")[2];
                const url = renderImage(
                  `machine/attachment/${data.attachments[key]}`
                );
                const attachment = {
                  name: name,
                  type,
                  url,
                  value: data.attachments[key],
                };
                att.push(attachment);
               
              }
            });
          setAttachments(att);
        
        }

        
      } else {
        toast.error("Failed to load meter data");
      }
      setLoading(false);
    } catch (error) {
      setLoading(false);
      toast.error("Failed to load meter data");
      console.error(error);
    }
  }

  const handleAddReading = async () => {
    try {
      if (!newReading.reading_value) {
        toast.error("Reading value is required");
        return;
      }

      const res = await meterReading.create({
        ...newReading,
        meter_id: id,
      });

      if (res.ok) {
        toast.success("Reading added successfully");
        setNewReading({
          reading_value: "",
          reading_date: new Date().toISOString().split('T')[0],
          notes: "",
        });
        setShowAddReading(false);
            getReadings()
      } else {
        toast.error("Failed to add reading");
      }
    } catch (error) {
      toast.error("Failed to add reading");
      console.error(error);
    }
  };

  async function getReadings() {
    try {
      setChartLoading(true);
      const dateRange = getDateRange(timeRange);
      const res = await meterReading.list({ 
        meter_id: id,
        start_date:   dateRange.start.toISOString(),
        end_date: dateRange.end.toISOString()
      });
      if (res.ok) {
        const body = await res.json();
        setReadings(body.data.reverse() || []);
      }
    } catch (error) {
      console.error("Failed to load readings:", error);
    } finally {
      setChartLoading(false);
    }
  }

  useEffect(() => {
    if (id) {
      getMeterData();
    }
  }, [id]);

  useEffect(() => {
    if (id) {
      getReadings();
    }
  }, [id, timeRange, customDateRange]);

  const chartData = {
    labels: readings.map(reading => format(new Date(reading.reading_date), 'dd MMM')),
    datasets: [
      {
        label: 'Readings',
        data: readings.map(reading => reading.reading_value),
        borderColor: '#007bff',
        backgroundColor: 'rgba(0, 123, 255, 0.1)',
        borderWidth: 2,
        pointBackgroundColor: '#007bff',
        pointBorderColor: '#fff',
        pointBorderWidth: 2,
        pointRadius: 4,
        pointHoverRadius: 6,
        tension: 0.1,
      },
      // Warning threshold line
      ...(meterData?.warning_threshold ? [{
        label: 'Warning Threshold',
        data: Array(readings.length).fill(parseFloat(meterData.warning_threshold)),
        borderColor: '#ffc107',
        backgroundColor: 'rgba(255, 193, 7, 0.2)',
        borderWidth: 3,
        borderDash: [8, 4],
        pointRadius: 0,
        tension: 0,
        fill: false,
      }] : []),
      // Alarm threshold line
      ...(meterData?.alarm_threshold ? [{
        label: 'Alarm Threshold',
        data: Array(readings.length).fill(parseFloat(meterData.alarm_threshold)),
        borderColor: '#dc3545',
        backgroundColor: 'rgba(220, 53, 69, 0.2)',
        borderWidth: 3,
        borderDash: [8, 4],
        pointRadius: 0,
        tension: 0,
        fill: false,
      }] : []),
    ],
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: {
      mode: 'index',
      intersect: false,
    },
    scales: {
      x: {
        grid: {
          color: 'rgba(255, 255, 255, 0.1)',
        },
        ticks: {
          color: '#6c757d',
        },
      },
      y: {
        grid: {
          color: 'rgba(255, 255, 255, 0.1)',
        },
        ticks: {
          color: '#6c757d',
        },
        title: {
          display: true,
          text: meterData?.unit?.name || 'CYCLES',
          color: '#6c757d',
        },
      },
    },
    plugins: {
      legend: {
        display: true,
        position: 'top',
        labels: {
          color: '#6c757d',
          usePointStyle: true,
          padding: 20,
          generateLabels: function(chart) {
            const datasets = chart.data.datasets;
            return datasets.map((dataset, index) => ({
              text: dataset.label,
              fillStyle: dataset.borderColor,
              strokeStyle: dataset.borderColor,
              lineWidth: dataset.borderWidth,
              lineDash: dataset.borderDash || [],
              pointStyle: index === 0 ? 'circle' : 'line',
              hidden: false,
              index: index
            }));
          }
        },
      },
      tooltip: {
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        titleColor: '#fff',
        bodyColor: '#fff',
        borderColor: '#007bff',
        borderWidth: 1,
        callbacks: {
          afterBody: function(context) {
            const readingValue = context[0]?.parsed?.y;
            const warningThreshold = meterData?.warning_threshold ? parseFloat(meterData.warning_threshold) : null;
            const alarmThreshold = meterData?.alarm_threshold ? parseFloat(meterData.alarm_threshold) : null;
            
            let status = '';
            if (alarmThreshold && readingValue >= alarmThreshold) {
              status = '🚨 ALARM THRESHOLD EXCEEDED';
            } else if (warningThreshold && readingValue >= warningThreshold) {
              status = '⚠️ WARNING THRESHOLD EXCEEDED';
            }
            
            return status ? [status] : [];
          }
        }
      },
    },
  };



  return (
    <div>
      <LoadingIndicator open={loading} />
      <ConfirmationDlg
        size={"md"}
        open={showCustomDateModal}
        onClose={() => setShowCustomDateModal(false)}
        btnSubmitText={"Apply"}
        btnCancelText={"Cancel"}
        onSubmit={handleCustomDateSubmit}
        title="Select Custom Date Range"
      >
        <div className="row">
          <div className="col-md-6">
            <div className="mb-3">
              <label className="form-label fw-bold">Start Date</label>
              <input
                type="date"
                className="form-control"
                value={customDateRange.start_date}
                onChange={(e) => setCustomDateRange({
                  ...customDateRange,
                  start_date: e.target.value
                })}
                max={customDateRange.end_date}
              />
            </div>
          </div>
          <div className="col-md-6">
            <div className="mb-3">
              <label className="form-label fw-bold">End Date</label>
              <input
                type="date"
                className="form-control"
                value={customDateRange.end_date}
                onChange={(e) => setCustomDateRange({
                  ...customDateRange,
                  end_date: e.target.value
                })}
                min={customDateRange.start_date}
                max={format(new Date(), 'yyyy-MM-dd')}
              />
            </div>
          </div>
        </div>
      </ConfirmationDlg>

      <ConfirmationDlg
        size={"lg"}
        open={showAddReading}
        onClose={() => setShowAddReading(false)}
        btnSubmitText={"Save Reading"}
        btnCancelText={"Cancel"}
        onSubmit={handleAddReading}
        title="Add New Reading"
      >
        <div className="row">
          <div className="col-md-6">
            <NormalInput
              required={true}
              value={newReading.reading_value}
              label="Reading Value"
              onchange={(e) => setNewReading({ ...newReading, reading_value: e.target.value })}
              name="reading_value"
              type="number"
              placeholder={`Enter reading in ${meterData?.unit?.name}`}
            />
          </div>
          <div className="col-md-6">
            <NormalInput
              required={true}
              value={newReading.reading_date}
              label="Reading Date"
              onchange={(e) => setNewReading({ ...newReading, reading_date: e.target.value })}
              name="reading_date"
              type="date"
            />
          </div>
        </div>
        <div className="row mt-3">
          <div className="col-12">
            <NormalTextArea
              value={newReading.notes}
              label="Notes"
              onchange={(e) => setNewReading({ ...newReading, notes: e.target.value })}
              name="notes"
              rows={3}
              placeholder="Optional notes about this reading"
            />
          </div>
        </div>
      </ConfirmationDlg>

      <div className="row">
        <div className="col-md-12">

        <Card>
            <CardHeader>
              <div className="d-flex justify-content-between align-items-center">
                <div>
                  <div className="d-flex align-items-center gap-2">
                    <h5 className="mb-0">Readings</h5>
                   
                  </div>
                  <small className="text-muted">
                    {meterData?.warning_threshold || meterData?.alarm_threshold ? 
                      "Dashed lines show warning (yellow) and alarm (red) thresholds" : 
                      "No thresholds configured"
                    }
                  </small>
                </div>
                <div className="btn-group btn-group-sm" role="group">
                  {timeRangeOptions.map((option) => (
                    <button
                      key={option.value}
                      type="button"
                      className={`btn ${timeRange === option.value ? 'btn-primary' : 'btn-outline-primary'}`}
                      onClick={() => handleTimeRangeClick(option.value)}
                    >
                      {option.label}
                    </button>
                  ))}
                </div>
              </div>
            </CardHeader>
            <CardBody>
             
              <div style={{ height: "300px", position: "relative" }}>
                {chartLoading ? (
                  <div className="d-flex justify-content-center align-items-center h-100">
                    <LoadingIndicator />
                  </div>
                ) : readings.length > 0 ? (
                  <Line data={chartData} options={chartOptions} />
                ) : (
                  <div className="d-flex justify-content-center align-items-center h-100">
                    <div className="text-center text-muted">
                      <i className="fa fa-chart-line fa-3x mb-3"></i>
                      <p>No readings available</p>
                    </div>
                  </div>
                )}
              </div>
              <div className="mt-3">
                {readings.length > 0 && (
                  <div className="mb-3 p-3 border rounded ">
                    <h6 className="mb-2">Current Status</h6>
                    <div className="row">
                      <div className="col-md-6">
                        <strong>Latest Reading:</strong> {readings[readings.length - 1]?.reading_value} {meterData?.unit?.name}
                        {readings.length > 0 && (() => {
                          const status = getReadingStatus(readings[readings.length - 1]?.reading_value);
                          return (
                            <span className={`ms-2 badge bg-${status.color}`}>
                              {status.icon} {status.status}
                            </span>
                          );
                        })()}
                      </div>
                    
                    </div>
                    
                  </div>
                )}

                <PermissionChecker permissions={["view meter reading"]} hideIfNotAllowed>
                <button
                  onClick={() => navigate(`/meter/readings/${id}`)}
                  className="btn btn-outline-primary btn-sm"
                  style={{marginRight: "10px"}}
                >
                  See All Readings <i className="fa fa-arrow-right ms-1"></i>
                  </button>
                  </PermissionChecker>

                <PermissionChecker permissions={["create meter reading"]} hideIfNotAllowed>
                <button
                  onClick={() => setShowAddReading(true)}
                  className="btn btn-outline-theme btn-sm me-2"
                >
                  Add Reading <i className="fa fa-plus ms-1"></i>
                  </button>
                  </PermissionChecker>

              </div>
            </CardBody>
          </Card>


          <Card>
            <CardHeader>
              Meter Information
            </CardHeader>
            <CardBody>
              <div className="row">
                <div className="col-md-6">
                  <div className="mb-3">
                    <label className="form-label fw-bold">Meter Name</label>
                    <p className="form-control-plaintext">{meterData?.meter_name}</p>
                  </div>


                  <div className="mb-3">
                    <label className="form-label fw-bold">Asset</label>
                    <p className="form-control-plaintext">
                      {meterData?.asset?.asset_name || "N/A"}
                    </p>
                  </div>

                

                  <div className="mb-3">
                    <label className="form-label fw-bold">Unit</label>
                    <p className="form-control-plaintext">{meterData?.unit?.name || "N/A"}</p>
                  </div>
                </div>

                <div className="col-md-6">
                  <div className="mb-3">
                    <label className="form-label fw-bold">Warning Threshold</label>
                    <p className="form-control-plaintext">
                      {meterData?.warning_threshold ? (
                        <Badge bg="warning">{meterData?.warning_threshold} {meterData?.unit?.name}</Badge>
                      ) : (
                        "Not set"
                      )}
                    </p>
                  </div>

                  <div className="mb-3">
                    <label className="form-label fw-bold">Alarm Threshold</label>
                    <p className="form-control-plaintext">
                      {meterData?.alarm_threshold ? (
                        <Badge bg="danger">{meterData?.alarm_threshold} {meterData?.unit?.name  }</Badge>
                      ) : (
                        "Not set"
                      )}
                    </p>
                  </div>
                </div>


                <div className="col-12">
                  <div className="mb-3">
                    <label className="form-label fw-bold">Description</label>
                    <p className="form-control-plaintext">
                      {meterData?.description || "No description available"}
                    </p>
                  </div>
                </div>
              </div>


              {attachments.length > 0 && (
            <div className="mb-3">
              <Card>
                <CardHeader className="d-flex align-items-center bg-inverse bg-opacity-10 fw-400">
                  Attachments
                </CardHeader>
                <CardBody>
                  <div className="row">
                    {attachments.map((attachment, index) => (
                      <div key={index} className={"col-sm-4 position-relative"}>
                        {attachment.type === "image" && (
                          <ImageViewer
                            staticImage={attachment.url}
                            className={"img-fluid w-100"}
                            alt=""
                          />
                        )}
                        {attachment.type === "video" && (
                          <video
                            src={attachment.url}
                            className={"img-fluid w-100"}
                            width={"100%"}
                            controls
                          />
                        )}
                        {attachment.type === "pdf" && (
                          <iframe
                            src={attachment.url}
                            className={" w-100"}
                            width={"100%"}
                            height={"350px"}
                          />
                        )}
                        {attachment.type === "other" && (
                          <a
                            href={attachment.url}
                            target={"_blank"}
                            rel="noreferrer"
                            style={{
                              height: "200px",
                              paddingTop: "95px",
                              display: "block",
                            }}
                            className={
                              "border-theme border-1 border w-100 text-center"
                            }
                          >
                            Download the File
                          </a>
                        )}

                        <div className="my-2">
                          <input
                            type="text"
                            className={"form-control"}
                            value={attachment.name}
                            readOnly
                            disabled
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                </CardBody>
              </Card>
            </div>
              )}


              <Card>
                <CardHeader>
                Work Orders for Meter Reading
                </CardHeader>
                <CardBody>
                  {
                    meterData?.readings.length > 0 && meterData?.readings.filter(reading => reading.work_order.length > 0).map((reading) => (
                      <div key={reading.id}>
                        <div className="row"> 
                          <div className="col-md-6">
                            <p className="text-bold">{reading.work_order[0].title} -  <span
          className={`badge border border-${getStatusColor(
            reading.work_order[0].status
          )} text-${getStatusColor(reading.work_order[0].status)}`}
        >
          {reading.work_order[0].status}
                            </span></p>
                            <p className="text-muted mb-0">Start Date: {formatDateOnly(new Date(reading.work_order[0].start_date))}</p>
                            <p className="text-muted mb-0">Due Date: {formatDateOnly(new Date(reading.work_order[0].due_date))}</p>
                            <p className="text-muted mb-0">Reading value: {reading.reading_value || '-'} {meterData.unit?.name}</p>
                            <p className="text-muted mb-0">Reading date: {formatDateOnly(new Date(reading.reading_date))}</p>
                            <p className="text-muted mb-0">Notes: {reading.notes || '-'}</p>

                          </div>
                          <div className="col-md-6">
                            <div className="text-end">
                              <button onClick={() => navigate(`/work-order/detail/${reading.work_order[0].id}`)} className="btn btn-primary">View Work Order</button>
                            </div>
                          </div>

                        </div>
                        <hr />
                      </div>
                    ))
                  }
                
                  {(meterData?.readings.length == 0 || meterData?.readings.every(reading => reading.work_order.length == 0)) && (
                  <div className="row">
                    <div className="col-md-12">
                      <div className="mb-3">
                      <div  className="text-center text-muted py-4 cursor-pointer">
                            <i className="fa fa-list fa-3x mb-3"></i>
                            <p>No work orders found for this meter.</p>
            <button onClick={() => navigate(`/work-order/add?meter_id=${id}`)} className="btn btn-outline-primary ">Add work orders to this meter.</button>
          </div>
                      </div>
                    </div>
                  </div>
                  )}
                </CardBody>
              </Card>
                


            </CardBody>
          </Card>
        </div>

       
      </div>
    </div>
  );
}

export default MeterDetail; 